import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const DAY_FULL = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

// Returns the next valid meal-prep delivery date + its ordering cutoff
function getNextMealPrepDelivery(rules) {
  const {
    meal_prep_delivery_days = [1],
    meal_prep_cutoff_day    = 5,
    meal_prep_cutoff_time   = '23:59',
  } = rules;

  const [cutoffHour, cutoffMin] = meal_prep_cutoff_time.split(':').map(Number);
  const now = new Date();

  for (let i = 1; i <= 21; i++) {
    const candidate = new Date(now);
    candidate.setDate(now.getDate() + i);
    candidate.setHours(0, 0, 0, 0);

    const dow = candidate.getDay();
    if (!meal_prep_delivery_days.includes(dow)) continue;

    const daysBack   = (dow - meal_prep_cutoff_day + 7) % 7;
    const cutoffDate = new Date(candidate);
    cutoffDate.setDate(candidate.getDate() - daysBack);
    cutoffDate.setHours(cutoffHour, cutoffMin, 0, 0);

    if (now <= cutoffDate) {
      return {
        label:       candidate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' }),
        cutoffLabel: cutoffDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' }),
      };
    }
  }
  return null;
}

// ── Order-type tile configuration ──────────────────────────────────────────
const TILES = [
  {
    id:          'meal_prep',
    emoji:       '🥗',
    label:       'Meal Prep',
    sublabel:    'Fresh weekly portions delivered to your door',
    bgIcon:      'bg-green-50',
    colorIcon:   'text-green-500',
    iconName:    'Coffee',
    ring:        'ring-green-100',
    border:      'border-green-200',
  },
  {
    id:          'meet_up',
    emoji:       '🤝',
    label:       'Meet-Up',
    sublabel:    'Sunday pick-up nights — no delivery fee',
    bgIcon:      'bg-blush-50',
    colorIcon:   'text-blush-400',
    iconName:    'MapPin',
    ring:        'ring-blush-100',
    border:      'border-blush-200',
  },
  {
    id:          'catering',
    emoji:       '🎉',
    label:       'Catering / Events',
    sublabel:    'Inquire about catering for your next event',
    bgIcon:      'bg-orange-50',
    colorIcon:   'text-orange-400',
    iconName:    'Star',
    ring:        'ring-orange-100',
    border:      'border-orange-200',
  },
  {
    id:          'corporate',
    emoji:       '🏢',
    label:       'Corporate / Workplace',
    sublabel:    'Recurring team meals — contact us to set up',
    bgIcon:      'bg-blue-50',
    colorIcon:   'text-blue-400',
    iconName:    'Briefcase',
    ring:        'ring-blue-100',
    border:      'border-blue-200',
  },
];

export default function Step0DeliveryType({ formData, updateFormData, onNext, orderRules = {}, meetUpEvents = [] }) {
  const nextMealPrep  = getNextMealPrepDelivery(orderRules);
  const minQty        = orderRules.meal_prep_min_qty ?? 5;
  const selected      = formData.delivery_type || null;

  // Next upcoming meet-up event (if any)
  const today = new Date().toISOString().split('T')[0];
  const nextMeetUp = meetUpEvents
    .filter(e => e.active && e.event_date >= today)
    .sort((a, b) => a.event_date.localeCompare(b.event_date))[0] || null;

  const handleSelect = (type) => {
    updateFormData({
      delivery_type: type,
      delivery_date: '',
      quantity:      type === 'meal_prep' ? (orderRules.meal_prep_min_qty ?? 5) : undefined,
      meet_up_event_id:    undefined,
      meet_up_event_title: undefined,
      meet_up_location:    undefined,
    });
  };

  const handleContinue = () => {
    if (!selected) return;
    onNext(selected); // MultiStepForm decides routing based on type
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-10"
    >
      {/* Header */}
      <div className="text-center space-y-3">
        <h2 className="text-4xl md:text-5xl font-serif text-charcoal">How Can We Help?</h2>
        <p className="text-sage-500 text-lg md:text-xl font-medium">
          Choose what type of order you'd like to place.
        </p>
      </div>

      {/* 2×2 grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
        {TILES.map((tile) => {
          const isSelected = selected === tile.id;

          return (
            <motion.button
              key={tile.id}
              whileHover={{ y: -4 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => handleSelect(tile.id)}
              className={`relative text-left rounded-[2.5rem] p-8 border-2 transition-all duration-300 focus:outline-none ${
                isSelected
                  ? `border-blush-300 bg-blush-50/30 shadow-lg ring-4 ring-blush-100`
                  : 'border-sage-100 bg-white hover:border-sage-200 hover:shadow-soft cursor-pointer'
              }`}
            >
              {/* Selected checkmark */}
              {isSelected && (
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-5 right-5 w-8 h-8 bg-blush-300 rounded-full flex items-center justify-center shadow-md"
                >
                  <SafeIcon name="Check" className="text-white text-sm" />
                </motion.div>
              )}

              <div className="space-y-4">
                {/* Icon */}
                <div className={`w-14 h-14 ${tile.bgIcon} rounded-2xl flex items-center justify-center`}>
                  <SafeIcon name={tile.iconName} className={`${tile.colorIcon} text-2xl`} />
                </div>

                {/* Label */}
                <div>
                  <h3 className="font-serif text-2xl text-charcoal">{tile.label}</h3>
                  <p className="text-sage-500 text-sm mt-1 font-medium">{tile.sublabel}</p>
                </div>

                {/* Dynamic info badge */}
                {tile.id === 'meal_prep' && (
                  nextMealPrep ? (
                    <div className="space-y-1.5">
                      <div className="bg-green-50 border border-green-100 rounded-2xl px-4 py-2.5">
                        <p className="text-green-700 font-bold text-sm">{nextMealPrep.label}</p>
                      </div>
                      <p className="text-sage-400 text-xs font-bold uppercase tracking-wider">
                        Order by {nextMealPrep.cutoffLabel}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sage-400 text-xs font-bold uppercase tracking-wider">
                      Ordering closed this week
                    </p>
                  )
                )}

                {tile.id === 'meet_up' && (
                  nextMeetUp ? (
                    <div className="space-y-1.5">
                      <div className="bg-blush-50 border border-blush-100 rounded-2xl px-4 py-2.5">
                        <p className="text-blush-600 font-bold text-sm">
                          {new Date(nextMeetUp.event_date + 'T00:00:00').toLocaleDateString('en-US', {
                            weekday: 'long', month: 'long', day: 'numeric',
                          })}
                        </p>
                      </div>
                      <p className="text-sage-400 text-xs font-bold uppercase tracking-wider">
                        {nextMeetUp.location}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-sage-50 border border-sage-100 rounded-2xl px-4 py-2.5">
                      <p className="text-sage-500 font-bold text-sm">No upcoming events</p>
                    </div>
                  )
                )}

                {tile.id === 'catering' && (
                  <div className="bg-orange-50 border border-orange-100 rounded-2xl px-4 py-2.5">
                    <p className="text-orange-600 font-bold text-sm">Inquiry only — we'll reach out</p>
                  </div>
                )}

                {tile.id === 'corporate' && (
                  <div className="bg-blue-50 border border-blue-100 rounded-2xl px-4 py-2.5">
                    <p className="text-blue-600 font-bold text-sm">Account setup required</p>
                  </div>
                )}

                {/* Sub-note */}
                {tile.id === 'meal_prep' && (
                  <p className="text-[10px] text-sage-400 uppercase tracking-widest font-bold">
                    Min. {minQty} portions per order
                  </p>
                )}
                {tile.id === 'meet_up' && (
                  <p className="text-[10px] text-green-600 uppercase tracking-widest font-bold">
                    No delivery fee
                  </p>
                )}
                {tile.id === 'catering' && (
                  <p className="text-[10px] text-sage-400 uppercase tracking-widest font-bold">
                    Birthdays · Weddings · Corporate
                  </p>
                )}
                {tile.id === 'corporate' && (
                  <p className="text-[10px] text-sage-400 uppercase tracking-widest font-bold">
                    Contact us to get started
                  </p>
                )}
              </div>
            </motion.button>
          );
        })}
      </div>

      {/* Continue button */}
      <div className="flex justify-center pt-2">
        <motion.button
          onClick={handleContinue}
          disabled={!selected}
          whileHover={selected ? { scale: 1.02 } : {}}
          whileTap={selected ? { scale: 0.98 } : {}}
          className="btn-primary w-full max-w-sm md:w-auto md:px-16 flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {selected === 'catering'   ? 'Tell Us About Your Event' :
           selected === 'corporate'  ? 'Learn How to Get Started' :
           'Continue'}
          <SafeIcon name="ArrowRight" />
        </motion.button>
      </div>
    </motion.div>
  );
}
